import { computed } from 'vue';
import { useStore } from 'vuex';
import UtdCache from '_acaSrc/utility/UtdCache';
import { logUiClickEventHelper } from '_acaSrc/utility/Events';
import { trackAndNavigateOpts } from '_acaSrc/plugins/UtdEventManager';

export default function useExpertAiAction() {
    const store = useStore();
    const isAnonymousUser = computed(() => store.getters['app/isAnonymousUser']);
    const utdAIURL = computed(() => store.getters['app/utdAIURL']);
    const isAnonymousExpertClinicianAccessEnabled = computed(() =>
        store.getters['feature/isAnonymousExpertClinicianAccessEnabled']);

    const createUtdAiRedirectUrl = ({
        queryParam = 'search',
        queryValue = ''
    } = {}) => {
        if (!utdAIURL.value) {
            console.warn('UpToDate AI URL not available.');
            return;
        }
        const params = new URLSearchParams();
        if (queryValue) {
            params.append(queryParam, queryValue);
        }
        params.append('sourceDomain', window.location.hostname);
        return `${utdAIURL.value}#${params.toString()}`;
    };

    const resolveQueryData = ({
        queryParam = 'search',
        searchTerm = '',
        prompt = ''
    } = {}) => {
        const selectedQueryParam = queryParam === 'prompt' ? 'prompt' : 'search';
        const selectedQueryValue = selectedQueryParam === 'prompt' ? prompt : searchTerm;
        return { selectedQueryParam, selectedQueryValue };
    };

    const getTrackingData = ({ selectedQueryParam, selectedQueryValue }) => {
        return selectedQueryParam === 'prompt'
            ? { prompt: selectedQueryValue }
            : { searchTerm: selectedQueryValue };
    };

    const shouldUseAnonymousFlow = () =>
        isAnonymousUser.value && !isAnonymousExpertClinicianAccessEnabled.value;

    const openWindow = ({ redirectUrl }) => {
        window.open(redirectUrl, '_self');
    };

    const handleAnonymousFlow = ({
        redirectUrl,
        uiElementName,
        trackingData,
        loginRegisterModalRef
    }) => {
        logUiClickEventHelper({ targetUrl: null, uiElementName, ...trackingData });
        const cache = new UtdCache();
        cache.setPersistent('isExpertAiRedirect', true, 1);
        cache.setPersistent('expertAiRedirectUrl', redirectUrl, 1);

        if (loginRegisterModalRef?.value?.open) {
            loginRegisterModalRef.value.open();
            return;
        }
        openWindow({ redirectUrl });
    };

    const handleTrackedNavigation = async({
        redirectUrl,
        uiElementName,
        trackingData
    }) => {
        await trackAndNavigateOpts(utdAIURL.value, uiElementName, trackingData);
        openWindow({ redirectUrl });
    };

    const handleExpertAiAction = async({
        event,
        searchTerm = '',
        prompt = '',
        queryParam = 'search',
        uiElementName,
        loginRegisterModalRef }) => {
        const queryData = resolveQueryData({ queryParam, searchTerm, prompt });
        const redirectUrl = createUtdAiRedirectUrl({
            queryParam: queryData.selectedQueryParam,
            queryValue: queryData.selectedQueryValue
        });
        if (!redirectUrl) {
            return;
        }
        const trackingData = getTrackingData(queryData);

        event.preventDefault();
        event.stopPropagation();

        if (shouldUseAnonymousFlow()) {
            handleAnonymousFlow({
                redirectUrl,
                uiElementName,
                trackingData,
                loginRegisterModalRef
            });
            return;
        }

        await handleTrackedNavigation({
            redirectUrl,
            uiElementName,
            trackingData
        });
    };

    return {
        handleExpertAiAction
    };
}

